import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInPath } from 'common/config/urls';
import { SIGN_IN_VIEW } from 'common/constants/auth';
import { REDIRECT_MS, SSO_ERROR_REDIRECTING_IN_5_SECONDS } from 'common/constants/sso';
import { ErrorComponent as TransitionComponent } from 'components';
import { useReadFragmentFromUrl } from 'hooks';
import { useGetUserInfo } from 'pages/Auth/hooks';
import {
  buildSignInUrl,
  clearOtpVerified,
  sanitizeReturnTo,
  setEnterpriseSsoSignInSession,
  setStoredAuthTokens,
} from 'pages/Auth/utils';
import { useExchangeToken } from './hooks';

// Minimal page that redeems SSO exchange ticket from fragment `#e=<ticket>`
const SsoExchange = () => {
  const navigate = useNavigate();
  const { exchangeToken: exchangeTokenFn, tokenExchangeHasFailed, isLoading } = useExchangeToken();
  const { fragmentRef } = useReadFragmentFromUrl();
  const getUserInfo = useGetUserInfo({ useGlobalLoading: false });
  // React StrictMode can run effects twice; the exchange ticket is single-use.
  const hasBeenRedeemed = useRef(false);

  useEffect(() => {
    if (!fragmentRef.current) {
      navigate(signInPath); // No ticket found, redirect to login
    }
    const redeem = async () => {
      const response = await exchangeTokenFn(fragmentRef.current!);
      if (response) {
        setStoredAuthTokens({
          accessToken: response.access,
          refreshToken: response.refresh,
        });
        // SSO creates a new session, so OTP state must be resolved by the post-auth flow.
        clearOtpVerified();
        setEnterpriseSsoSignInSession();
        await getUserInfo();
        navigate(
          buildSignInUrl({
            returnTo: sanitizeReturnTo(response.redirect_to),
            view: SIGN_IN_VIEW.WORKSPACE_SELECTION,
          }),
          { replace: true }
        );
      }
    };
    if (!hasBeenRedeemed.current && fragmentRef.current) {
      hasBeenRedeemed.current = true;
      redeem();
    }
  }, [navigate, exchangeTokenFn, getUserInfo, fragmentRef]);

  useEffect(() => {
    if (tokenExchangeHasFailed) {
      // set timeout to redirect to login page after showing error
      const timer = setTimeout(() => {
        navigate(signInPath);
      }, REDIRECT_MS);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [tokenExchangeHasFailed, navigate]);

  if (!isLoading && tokenExchangeHasFailed) {
    return <TransitionComponent title=":(" subtitle="Couldn't sign you in with SSO" body={SSO_ERROR_REDIRECTING_IN_5_SECONDS} />;
  }

  return <TransitionComponent title="..." subtitle="Processing SSO response" body="Please wait a moment..." />;
};

export default SsoExchange;
